import { Divider, Flex, Group, ScrollArea, Stack, TextInput } from '@mantine/core';
import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Outcome } from '../../../../../api/strategy-client/strategy-client.type';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import { useWindowSize } from '../../../../../hooks/useWindowSize';
import { useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { HEIGHT_BREAKPOINTS } from '../../../../../styles/breakpoints';
import { baseWhite, inkLight, secondaryBase, skyLighter } from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Checkbox } from '../../../../../ui-library/checkbox/checkbox';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../ui-library/typography/typography';
import { useEpicsSearch } from './milestone-modal.hooks';
import type { MilestoneModalMode } from './milestone-modal.type';

type Props = {
  mode: MilestoneModalMode;
  selectedEpics: Epic[];
  setSelectedEpics: React.Dispatch<React.SetStateAction<Epic[]>>;
  availableEpics: Epic[];
  onBack: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
};

export function MilestoneModalStepTwo({
  mode,
  selectedEpics,
  setSelectedEpics,
  availableEpics,
  onBack,
  onCancel,
  onSubmit,
  isSubmitting,
}: Props) {
  const i18n = useTranslation();
  const { height } = useWindowSize();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const initiativePerformance = useStrategyStore((state) => state.initiativePerformance);

  const initiatives = (initiativePerformance as any as Outcome)?.initiatives || [];

  const { filteredEpics, handleSearchChange: handleEpicsSearch } = useEpicsSearch(availableEpics);

  // Group epics by initiative using the epic_ids field
  const groupedEpics = useMemo(() => {
    const groups: Record<string, Epic[]> = {};

    filteredEpics.forEach((epic) => {
      // Find which initiative(s) contain this epic by checking their epic_ids array
      const initiativesContainingEpic = initiatives.filter((initiative: any) => initiative.epic_ids?.includes(epic.id));

      if (initiativesContainingEpic.length > 0) {
        // Group by each initiative that contains this epic
        initiativesContainingEpic.forEach((initiative: any) => {
          const initiativeName = initiative.name;
          if (!groups[initiativeName]) {
            groups[initiativeName] = [];
          }
          groups[initiativeName].push(epic);
        });
      } else {
        // If epic doesn't belong to any initiative, put in 'Ungrouped'
        if (!groups['Ungrouped']) {
          groups['Ungrouped'] = [];
        }
        groups['Ungrouped'].push(epic);
      }
    });

    return groups;
  }, [filteredEpics, initiatives]);

  const handleEpicCheckboxChange = (epic: Epic, event: ChangeEvent<HTMLInputElement>) => {
    setSelectedEpics((prev) => (event.target.checked ? [...prev, epic] : prev.filter((e) => e.id !== epic.id)));
  };

  const handleGroupSelectAll = (epicsInGroup: Epic[], event: ChangeEvent<HTMLInputElement>) => {
    const allGroupEpicsSelected = epicsInGroup.every((e) => selectedEpics.some((se) => se.id === e.id));

    if (event.target.checked || !allGroupEpicsSelected) {
      // Select all
      const newEpics = epicsInGroup.filter((e) => !selectedEpics.some((se) => se.id === e.id));
      setSelectedEpics((prev) => [...prev, ...newEpics]);
    } else {
      // Deselect all
      setSelectedEpics((prev) => prev.filter((e) => !epicsInGroup.some((ge) => ge.id === e.id)));
    }
  };

  const isGroupFullySelected = (epicsInGroup: Epic[]): boolean => {
    return epicsInGroup.length > 0 && epicsInGroup.every((e) => selectedEpics.some((se) => se.id === e.id));
  };

  const isGroupPartiallySelected = (epicsInGroup: Epic[]): boolean => {
    return epicsInGroup.some((e) => selectedEpics.some((se) => se.id === e.id)) && !isGroupFullySelected(epicsInGroup);
  };

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const labels = {
    description: i18n.t('strategy.milestones.form.epic_description', {
      defaultValue: `Select the ${i18n.t('common.parent_task').toLowerCase()}s that this milestone will track.`,
    }),
    searchPlaceholder: i18n.t('strategy.milestones.form.search_and_select_epics', {
      defaultValue: `Search and select ${i18n.t('common.parent_task').toLowerCase()}s`,
    }),
    noItemsFound: i18n.t('strategy.filters.no_epics_found', {
      defaultValue: `No ${i18n.t('common.parent_task').toLowerCase()}s found`,
    }),
    back: i18n.t('strategy.milestones.form.back', { defaultValue: 'Back' }),
    cancel: i18n.t('strategy.milestones.form.cancel', { defaultValue: 'Cancel' }),
    submit:
      mode === 'edit'
        ? i18n.t('strategy.milestones.form.save_changes', { defaultValue: 'Save Changes' })
        : i18n.t('strategy.milestones.form.complete_and_create', { defaultValue: 'Complete and Create' }),
  };

  const isAtLeastOneEpicSelected = selectedEpics.length > 0;

  return (
    <Stack gap={16} p={0}>
      <Text>{labels.description}</Text>

      <TextInput
        placeholder={labels.searchPlaceholder}
        radius="xl"
        size="sm"
        leftSection={<Icon name="search" size={16} color={inkLight} />}
        onChange={(event) => {
          handleEpicsSearch(event.currentTarget.value);
        }}
      />

      <Divider />

      <ScrollArea type="always" h={height > parseInt(HEIGHT_BREAKPOINTS.md, 10) ? 336 : 240} scrollbarSize={8}>
        <Stack>
          {filteredEpics.length === 0 ? (
            <Text size="small" color={inkLight} style={{ textAlign: 'center' }}>
              {labels.noItemsFound}
            </Text>
          ) : (
            <Flex direction="column" gap="md">
              {Object.entries(groupedEpics).map(([initiativeName, epicsInGroup]) => {
                const isCollapsed = collapsedGroups[initiativeName];
                const fullySelected = isGroupFullySelected(epicsInGroup);
                const partiallySelected = isGroupPartiallySelected(epicsInGroup);

                return (
                  <Stack key={initiativeName} gap="md">
                    {/* Initiative Header with Select All and Collapse */}
                    <Group
                      justify="space-between"
                      align="center"
                      p="xs"
                      style={{ backgroundColor: skyLighter, borderRadius: 6 }}
                    >
                      <Group gap={8} flex={1}>
                        <Icon
                          name={isCollapsed ? 'chevron_right' : 'expand_more'}
                          size={20}
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleGroup(initiativeName)}
                        />
                        <Checkbox
                          indeterminate={partiallySelected}
                          checked={fullySelected}
                          color={secondaryBase}
                          onChange={(event) => handleGroupSelectAll(epicsInGroup, event)}
                        />
                        <Text weight="medium" size="small" style={{ flex: 1 }}>
                          {initiativeName}
                        </Text>
                        <Text size="small" color={inkLight}>
                          ({epicsInGroup.filter((e) => selectedEpics.some((se) => se.id === e.id)).length}/
                          {epicsInGroup.length})
                        </Text>
                      </Group>
                    </Group>

                    {!isCollapsed && (
                      <Stack gap={8} pl={16}>
                        {epicsInGroup.map((epic: Epic) => {
                          const isSelected = selectedEpics.some((e) => e.id === epic.id);
                          return (
                            <Checkbox
                              key={epic.id}
                              checked={isSelected}
                              onChange={(event) => handleEpicCheckboxChange(epic, event)}
                              label={
                                <Stack gap={2}>
                                  <Text weight="medium" size="small">
                                    {epic.name}
                                  </Text>
                                </Stack>
                              }
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Flex>
          )}
        </Stack>
      </ScrollArea>

      <Divider />

      <Group justify="space-between">
        <Button
          radius="xl"
          variant="outline"
          onClick={onBack}
          leftSection={<Icon name="arrow_back" size={16} color={inkLight} pointer />}
        >
          {labels.back}
        </Button>
        <Group gap={16}>
          <Button radius="xl" variant="outline" onClick={onCancel}>
            {labels.cancel}
          </Button>
          <Tooltip
            zIndex={1000}
            label={isAtLeastOneEpicSelected ? 'Submit' : `Please select at least one ${i18n.t('common.parent_task')}`}
            variant="black"
          >
            <Button
              radius="xl"
              onClick={onSubmit}
              loading={isSubmitting}
              disabled={!isAtLeastOneEpicSelected}
              rightSection={<Icon name="task_alt" size={16} color={baseWhite} pointer />}
            >
              {labels.submit}
            </Button>
          </Tooltip>
        </Group>
      </Group>
    </Stack>
  );
}
